export type ThemeMode = 'light' | 'dark';

export class ThemeManager {
  private static readonly STORAGE_KEY = 'crm-theme';
  private currentTheme: ThemeMode = 'light';
  private mediaQuery: MediaQueryList | null = null;
  private transitionTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.initTheme();
  }

  private initTheme(): void {
    if (typeof window !== 'undefined' && 'matchMedia' in window) {
      this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    }

    const saved = this.readStoredTheme();
    const systemTheme = this.getSystemTheme();

    // Use saved preference if present, otherwise follow system preference
    this.currentTheme = saved ?? systemTheme;
    // Don't animate initial page render to prevent color flash on load
    this.applyTheme(this.currentTheme, false);

    // Listen dynamically for system preference changes across all browsers including Chrome
    if (this.mediaQuery) {
      const handleSystemChange = () => {
        // Automatically switch theme if user hasn't saved an explicit preference override
        if (!this.readStoredTheme()) {
          this.currentTheme = this.getSystemTheme();
          this.applyTheme(this.currentTheme, true);
        }
      };

      try {
        if (typeof this.mediaQuery.addEventListener === 'function') {
          this.mediaQuery.addEventListener('change', handleSystemChange);
        } else if (typeof (this.mediaQuery as any).addListener === 'function') {
          (this.mediaQuery as any).addListener(handleSystemChange);
        }
      } catch {
        try {
          (this.mediaQuery as any).addListener(handleSystemChange);
        } catch {
          // Ignore if matchMedia event listeners unsupported
        }
      }
    }
  }

  public getTheme(): ThemeMode {
    return this.currentTheme;
  }

  public getSystemTheme(): ThemeMode {
    if (!this.mediaQuery && typeof window !== 'undefined' && 'matchMedia' in window) {
      this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    }
    return this.mediaQuery?.matches ? 'dark' : 'light';
  }

  public setTheme(theme: ThemeMode): void {
    this.currentTheme = theme;

    // If selected theme matches system setting, clear stored preference to resume auto-following system
    if (theme === this.getSystemTheme()) {
      this.clearStoredTheme();
    } else {
      this.persistTheme(theme);
    }

    this.applyTheme(theme, true);
  }

  public toggleTheme(): ThemeMode {
    const next = this.currentTheme === 'dark' ? 'light' : 'dark';
    this.setTheme(next);
    return next;
  }

  private applyTheme(theme: ThemeMode, animate = true): void {
    if (animate && typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.classList.add('theme-transitioning');
      // Force Chrome reflow so CSS transitions take effect in Chromium engine
      void document.documentElement.offsetHeight;

      if (this.transitionTimeout) {
        clearTimeout(this.transitionTimeout);
      }
      this.transitionTimeout = setTimeout(() => {
        document.documentElement.classList.remove('theme-transitioning');
      }, 500);
    }

    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.style.colorScheme = theme;

    // Update theme toggle buttons iconography
    const toggleBtns = document.querySelectorAll<HTMLButtonElement>('.theme-toggle-btn');
    toggleBtns.forEach((btn) => {
      const sunIcon = btn.querySelector('.theme-icon-sun');
      const moonIcon = btn.querySelector('.theme-icon-moon');
      if (sunIcon && moonIcon) {
        if (theme === 'dark') {
          sunIcon.classList.remove('hidden');
          moonIcon.classList.add('hidden');
        } else {
          sunIcon.classList.add('hidden');
          moonIcon.classList.remove('hidden');
        }
      }
    });
  }

  private readStoredTheme(): ThemeMode | null {
    if (typeof localStorage === 'undefined') return null;
    try {
      const saved = localStorage.getItem(ThemeManager.STORAGE_KEY);
      return saved === 'dark' || saved === 'light' ? saved : null;
    } catch {
      return null;
    }
  }

  private persistTheme(theme: ThemeMode): void {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(ThemeManager.STORAGE_KEY, theme);
    } catch {
      // Ignore storage errors
    }
  }

  private clearStoredTheme(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.removeItem(ThemeManager.STORAGE_KEY);
    } catch {
      // Ignore storage errors
    }
  }
}


